import i18n from 'i18next';
import { Dispatch } from 'redux';
import {
  deleteRequest,
  getRequest,
  postRequest,
  putRequest
} from 'src/store/api-handler';
import {
  ParameterSetDto,
  ParameterSetEditDto,
  ParameterSetTypeDto
} from 'src/store/parametersets/parameterset';
import { alertSuccess } from '../alerts/alerts.actions';
import { requestBegin, requestEnd } from '../requests/requests.actions';

export enum ParameterSetTypes {
  FetchAll = 'parameterSets/FetchAll',
  Add = 'parameterSets/Add',
  Update = 'parameterSets/Update',
  Delete = 'parameterSets/Delete',
  Failure = 'parameterSets/Failure',
  FetchTypes = 'parameterSets/FetchTypes'
}

export type ParameterSetAction =
  | { type: ParameterSetTypes.FetchAll; parameterSets: ParameterSetDto[] }
  | { type: ParameterSetTypes.Add; parameterSet: ParameterSetDto }
  | { type: ParameterSetTypes.Update; parameterSet: ParameterSetDto }
  | { type: ParameterSetTypes.Delete; parameterSet: ParameterSetDto }
  | { type: ParameterSetTypes.Failure; errors: any }
  | {
      type: ParameterSetTypes.FetchTypes;
      parameterSetTypes: ParameterSetTypeDto[];
    };

export const actionCreators = {
  fetchParameterSets: (
    parameterSets: ParameterSetDto[]
  ): ParameterSetAction => ({
    type: ParameterSetTypes.FetchAll,
    parameterSets
  }),
  addParameterSet: (parameterSet: ParameterSetDto): ParameterSetAction => ({
    type: ParameterSetTypes.Add,
    parameterSet
  }),
  updateParameterSet: (parameterSet: ParameterSetDto): ParameterSetAction => ({
    type: ParameterSetTypes.Update,
    parameterSet
  }),
  deleteParameterSet: (parameterSet: ParameterSetDto): ParameterSetAction => ({
    type: ParameterSetTypes.Delete,
    parameterSet
  }),
  parameterSetRequestFailure: (errors: any): ParameterSetAction => ({
    type: ParameterSetTypes.Failure,
    errors
  }),
  fetchTypes: (
    parameterSetTypes: ParameterSetTypeDto[]
  ): ParameterSetAction => ({
    type: ParameterSetTypes.FetchTypes,
    parameterSetTypes
  })
};

export const fetchParameterSets = () => {
  return async (dispatch: Dispatch) => {
    dispatch(requestBegin(ParameterSetTypes.FetchAll));
    const { ok, data, error } = await getRequest<ParameterSetDto[]>(
      '/parametersets'
    );
    dispatch(requestEnd(ParameterSetTypes.FetchAll));
    return ok
      ? dispatch(actionCreators.fetchParameterSets(data))
      : dispatch(actionCreators.parameterSetRequestFailure(error));
  };
};

export const createParameterSet = (parameterSet: ParameterSetEditDto) => {
  return async (dispatch: Dispatch) => {
    const { ok, error } = await postRequest('/parametersets', parameterSet);
    return ok
      ? dispatch(alertSuccess(i18n.t('parameterSet.created')))
      : dispatch(actionCreators.parameterSetRequestFailure(error));
  };
};

export const editParameterSet = (
  id: number,
  parameterSet: ParameterSetEditDto
) => {
  return async (dispatch: Dispatch) => {
    const { ok, error } = await putRequest(
      `/parametersets/${id}`,
      parameterSet
    );
    return ok
      ? dispatch(alertSuccess(i18n.t('parameterSet.updated')))
      : dispatch(actionCreators.parameterSetRequestFailure(error));
  };
};

export const deleteParameterSet = (
  id: number,
  parameterSet: ParameterSetDto
) => {
  return async (dispatch: Dispatch) => {
    const { ok, error } = await deleteRequest(
      `/parametersets/${id}`,
      parameterSet
    );
    if (ok) {
      dispatch(alertSuccess(i18n.t('parameterSet.deleted')));
      return dispatch(actionCreators.deleteParameterSet(parameterSet));
    }
    return dispatch(actionCreators.parameterSetRequestFailure(error));
  };
};

export const fetchParameterSetTypes = () => {
  return async (dispatch: Dispatch) => {
    const { ok, data, error } = await getRequest<ParameterSetTypeDto[]>(
      '/parametersets/types'
    );
    return ok
      ? dispatch(actionCreators.fetchTypes(data))
      : dispatch(actionCreators.parameterSetRequestFailure(error));
  };
};
